import {
  fetchMFAPreference,
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
  type FetchMFAPreferenceOutput
} from "aws-amplify/auth";
import { MFA_METHOD, MFA_POLICY, MFA_POLICY_ROLES, MFA_TOTP_ISSUER } from "src/components/constant/constant";
import { isCookieAuthMode } from "src/config/Config";
import { getMfaPreference, setMfaPreference, setupTotp, verifyTotp } from "src/network/authService";
import type { MfaMethod, MfaPolicy, MfaPolicyValue } from "src/types/commonTypes";

export const getRoleMfaPolicy = (
  policy: MfaPolicy | null | undefined,
  role: string | null | undefined
): MfaPolicyValue => (role && policy?.[role]) || MFA_POLICY.OPTIONAL;

// Only the PREFERRED method is challenged at sign-in; an enabled-but-not-preferred one is treated as off.
export const getPreferredMfaMethod = (preference: FetchMFAPreferenceOutput): MfaMethod | null => {
  if (preference.preferred === MFA_METHOD.TOTP) return MFA_METHOD.TOTP;
  if (preference.preferred === MFA_METHOD.EMAIL) return MFA_METHOD.EMAIL;
  return null;
};

// Cookie mode reads the preference through the BFF auth API; token mode asks Amplify directly.
export const fetchPreferredMfaMethod = async (): Promise<MfaMethod | null> => {
  if (isCookieAuthMode()) {
    const preference = await getMfaPreference();

    return preference.preferred ?? null;
  }

  return getPreferredMfaMethod(await fetchMFAPreference());
};

export type TotpEnrolment = { secret: string; setupUri: string };

export const startTotpEnrolment = async (accountName: string): Promise<TotpEnrolment> => {
  if (isCookieAuthMode()) {
    const setup = await setupTotp();

    return { secret: setup.secret, setupUri: setup.otpauthUri };
  }

  const totp = await setUpTOTP();

  return { secret: totp.sharedSecret, setupUri: totp.getSetupUri(MFA_TOTP_ISSUER, accountName).toString() };
};

export const verifyTotpCode = (code: string) => (isCookieAuthMode() ? verifyTotp(code) : verifyTOTPSetup({ code }));

export const formatTotpKey = (secret: string) => secret.replace(/(.{4})/g, "$1 ").trim();

export const getErrorName = (error: unknown) =>
  error && typeof error === "object" && "name" in error ? String((error as { name: unknown }).name) : "";

export const getErrorMessage = (error: unknown) =>
  error && typeof error === "object" && "message" in error ? String((error as { message: unknown }).message) : "";

// One method at a time: the chosen one is PREFERRED and the other DISABLED; null turns both off.
export const setPreferredMfaMethod = (method: MfaMethod | null) => {
  if (isCookieAuthMode()) return setMfaPreference(method);

  return updateMFAPreference({
    totp: method === MFA_METHOD.TOTP ? "PREFERRED" : "DISABLED",
    email: method === MFA_METHOD.EMAIL ? "PREFERRED" : "DISABLED"
  });
};

export const getMfaRoleLabel = (role: string | null | undefined) =>
  MFA_POLICY_ROLES.find((item) => item.role === role)?.label;
